import { computed, Component, inject } from "@angular/core";

// EZ
import { DocMeta } from "@template-ui/decorators";
import { InputConfig } from "@template-ui/types";
import { UiLibraryModule } from "@template-ui/template-ui.module";

import { AppBaseComponent } from "../../app-base.component";
import { ActivityLogGridComponent } from "../activity-log-grid.component";
import { ActivityLogGridConfigService } from "../activity-log-grid-config.service";
import { ActivityLogType } from "../activity-log-grid.types";
import { SHOWCASE_LIBRARY_NAMES } from "@app/shared/showcase/showcase-library.constants";

@DocMeta({
  title: "Sample ActivityLogGridSampleComponent",
  hide: true,  // Don't show in showcase navigation
  showcaseTitle: "Preset Selector Sample",
  showcaseAttachTo: [{ libName: SHOWCASE_LIBRARY_NAMES.APP, classNames: ["ActivityLogGridComponent"] }],
})
@Component({
  selector: "app-activity-log-sample",
  imports: [UiLibraryModule, ActivityLogGridComponent],
  templateUrl: "./activity-log-sample.component.html"
})
export class ActivityLogGridSampleComponent extends AppBaseComponent  {
  protected readonly defaultActivityLogType = ActivityLogType.CommutationInvestigation;

  protected readonly activityLogTypeOptions = [
    {
      value: ActivityLogType.CommutationInvestigation,
      viewValue: "Commutation Investigation",
      requiredGridContextKeys: ["caseId"] as const,
      requiredAddOnlyContextKeys: [] as const,
      hasAddActivityEndpoint: true
    },
    {
      value: ActivityLogType.BphFullInvestigation,
      viewValue: "BPH Full Investigation",
      requiredGridContextKeys: ["caseId"] as const,
      requiredAddOnlyContextKeys: [] as const,
      hasAddActivityEndpoint: true
    },
    {
      value: ActivityLogType.LegalDecisionReview,
      viewValue: "Legal Decision Review",
      requiredGridContextKeys: ["cdcNum"] as const,
      requiredAddOnlyContextKeys: ["planId"] as const,
      hasAddActivityEndpoint: true
    },
  ] as const;

  protected selectorAndGridFormValues: Record<string, unknown> = {
    activityLogType: this.defaultActivityLogType,
    caseId: 1,
    cdcNum: "H97093",
    hideAddActivity: false,
  };

  protected addOnlyContextFormValues: Record<string, unknown> = {
    planId: 1,
  };
  protected readonly showCaseId = computed(() => this.requiresCaseId);
  protected readonly showCdcNum = computed(() => this.requiresCdcNum);
  protected readonly showPlanId = computed(() => this.requiresPlanId);

  protected selectorAndGridInputConfigs: InputConfig[] = [
    {
      type: "select",
      name: "activityLogType",
      label: "Activity Log Type",
      width: "2x",
      row: 1,
      options: this.activityLogTypeOptions.map((option) => ({ value: option.value, viewValue: option.viewValue })),
    },
    {
      type: "checkbox",
      name: "hideAddActivity",
      label: "Hide Add Activity Button",
      width: "1x",
      row: 1,
    },
    {
      type: "number",
      name: "caseId",
      label: "Case ID",
      width: "1x",
      row: 1,
      show: this.showCaseId,
    },
    {
      type: "text",
      name: "cdcNum",
      label: "CDC #",
      width: "1x",
      row: 1,
      show: this.showCdcNum,
    },
  ];

  protected addOnlyContextInputConfigs: InputConfig[] = [
    {
      type: "number",
      name: "planId",
      label: "Plan ID",
      width: "1x",
      row: 1,
      show: this.showPlanId,
    },
  ];

  protected activityLogContext: { caseId?: number; cdcNum?: string; planId?: number } = { caseId: 1 };

  private readonly activityLogGridConfigService = inject(ActivityLogGridConfigService);

  constructor() {
    super();
    this.syncDerivedValues();
  }

  protected onSelectorAndGridFormValuesChange(formValues: Record<string, unknown>): void {
    this.selectorAndGridFormValues = formValues;
    this.syncDerivedValues();
  }

  protected onAddOnlyContextFormValuesChange(formValues: Record<string, unknown>): void {
    this.addOnlyContextFormValues = formValues;
    this.syncDerivedValues();
  }

  protected get selectedActivityLogType(): ActivityLogType {
    const value = this.selectorAndGridFormValues["activityLogType"];
    return this.isActivityLogType(value) ? value : this.defaultActivityLogType;
  }

  protected get requiredGridContextKeys(): readonly string[] {
    const selectedOption = this.activityLogTypeOptions.find((option) => option.value === this.selectedActivityLogType);
    return selectedOption?.requiredGridContextKeys ?? ["caseId"];
  }

  protected get requiredAddOnlyContextKeys(): readonly string[] {
    const selectedOption = this.activityLogTypeOptions.find((option) => option.value === this.selectedActivityLogType);
    return selectedOption?.requiredAddOnlyContextKeys ?? [];
  }

  protected get requiresCaseId(): boolean {
    return this.requiredGridContextKeys.includes("caseId");
  }

  protected get requiresCdcNum(): boolean {
    return this.requiredGridContextKeys.includes("cdcNum");
  }

  protected get requiresPlanId(): boolean {
    return this.requiredAddOnlyContextKeys.includes("planId");
  }

  protected get showAddOnlyParamsForm(): boolean {
    return this.requiredAddOnlyContextKeys.length > 0 && this.hasAddActivityEndpoint && !this.hideAddActivity;
  }

  protected get hideAddActivity(): boolean {
    return !!this.selectorAndGridFormValues["hideAddActivity"];
  }

  protected get hasAddActivityEndpoint(): boolean {
    const selectedOption = this.activityLogTypeOptions.find((option) => option.value === this.selectedActivityLogType);
    if (!selectedOption?.hasAddActivityEndpoint) {
      return false;
    }

    const context = this.getResolvedActivityLogContext();
    return !!this.activityLogGridConfigService.getConfig(this.selectedActivityLogType, context).addActivityEndpoint;
  }

  private getResolvedActivityLogContext(): { caseId?: number; cdcNum?: string; planId?: number } {
    const context: { caseId?: number; cdcNum?: string; planId?: number } = {};

    if (this.requiresCaseId) {
      context.caseId = this.toPositiveInt(this.selectorAndGridFormValues["caseId"]);
    }

    if (this.requiresCdcNum) {
      context.cdcNum = this.toStringOrDefault(this.selectorAndGridFormValues["cdcNum"], "H97093");
    }

    if (this.requiresPlanId) {
      context.planId = this.toPositiveInt(this.addOnlyContextFormValues["planId"]);
    }

    return context;
  }

  private syncDerivedValues(): void {
    this.activityLogContext = this.getResolvedActivityLogContext();
  }

  private isActivityLogType(value: unknown): value is ActivityLogType {
    return Object.values(ActivityLogType).includes(value as ActivityLogType);
  }

  private toPositiveInt(value: unknown): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }

  private toStringOrDefault(value: unknown, fallback: string): string {
    return typeof value === "string" && value.trim().length > 0 ? value : fallback;
  }
}
